// @ts-check
import { useEffect, useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { cn } from "@/lib/utils"

/** The four steps the user sees; several server stages share a step. */
const STEPS = [
  { label: "Read", stages: ["reading"] },
  { label: "Review", stages: ["thinking", "searching"] },
  { label: "Suggest", stages: ["drafting_edits", "saving_edits", "edits_saved"] },
  { label: "Summarise", stages: ["writing_summary"] },
]

/** How long the line stays on one search when several run at the same time. */
const SECONDS_PER_SEARCH = 2

/** @param {number} count @param {string} word */
function plural(count, word) {
  return `${count} ${word}${count === 1 ? "" : "s"}`
}

/**
 * @param {import("@/services/chat-provider/canvas/reviewProgress").ReviewProgress} progress
 * @param {number} [seconds] time since the review started
 */
export function stageText(progress, seconds = 0) {
  switch (progress.stage) {
    case "reading":
      return "Reading the paper"
    case "thinking":
      return progress.section ? `Reviewing · ${progress.section}` : "Reviewing the paper"
    case "searching": {
      const { queries } = progress
      if (queries.length === 0) return "Searching the web"
      return `Searching · ${queries[Math.floor(seconds / SECONDS_PER_SEARCH) % queries.length]}`
    }
    case "drafting_edits":
      return progress.drafted.length > 0
        ? `Writing suggestions · ${progress.drafted.length}`
        : "Writing suggestions"
    case "saving_edits":
      return "Adding the suggestions to the document"
    case "edits_saved": {
      const saved = progress.saved
      if (saved == null) return "Suggestions added"
      if (saved.failed) return "The suggestions could not be added"
      const added = `${plural(saved.added, "suggestion")} added`
      return saved.skipped > 0 ? `${added} · ${saved.skipped} did not match the text` : added
    }
    case "writing_summary":
      return "Writing the summary"
    default:
      return "Working on the review"
  }
}

/** @param {number} seconds */
function formatSeconds(seconds) {
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s`
}

/**
 * Live status of a canvas review while it streams: the current step, a short
 * description, and the time since the review started.
 * @param {{
 *   progress: import("@/services/chat-provider/canvas/reviewProgress").ReviewProgress,
 *   text?: string,
 *   className?: string,
 *   showSteps?: boolean,
 * }} props
 */
export function ReviewProgressLine({ progress, text: textOverride, className, showSteps = true }) {
  const reduceMotion = useReducedMotion()
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const seconds = Math.max(0, Math.round((now - progress.startedAt) / 1000))
  const currentStep = STEPS.findIndex(step => step.stages.includes(progress.stage))
  const text = textOverride ?? stageText(progress, seconds)
  const offset = reduceMotion ? 0 : 4

  return (
    // Sticky by default, so it stays in view while the live reasoning grows below it.
    <div className={className ?? "sticky top-0 z-10 -mx-1 mb-3 flex flex-col gap-2 rounded-lg bg-background/90 px-1 py-2 backdrop-blur-sm"}>
      <div className="flex items-center gap-2.5 text-sm">
        <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
          {!reduceMotion && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#34d094]/50" />
          )}
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#34d094]" />
        </span>
        <span className="min-w-0 truncate" role="status" aria-live="polite">
          {/* Fade in only: the text can change several times a second, and an
              exit animation in "wait" mode jams on the old text. */}
          <motion.span
            key={text}
            className="inline-block text-foreground/85"
            initial={{ opacity: 0, y: offset }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            {text}
          </motion.span>
        </span>
        <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground" aria-label={`${seconds} seconds`}>
          {formatSeconds(seconds)}
        </span>
      </div>

      {showSteps && <ol className="flex items-center gap-1.5" aria-label="Review steps">
        {STEPS.map((step, index) => (
          <li key={step.label} className="flex flex-1 flex-col gap-1">
            <span
              className={cn(
                "h-1 rounded-full transition-colors duration-300",
                index < currentStep && "bg-[#34d094]",
                index === currentStep && "bg-[#34d094]/60",
                (currentStep === -1 || index > currentStep) && "bg-muted-foreground/20",
              )}
            />
            <span
              className={cn(
                "text-[11px] leading-none",
                index <= currentStep ? "text-foreground/70" : "text-muted-foreground/60",
              )}
            >
              {step.label}
            </span>
          </li>
        ))}
      </ol>}
    </div>
  )
}
